import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { AesEncrypt, AesDecrypt } from "../../../services/crypto";
import WebService from "../../../services/webservice";
import Swal from 'sweetalert2';
import { Pagination, PaginationItem, PaginationLink } from "reactstrap";
import { ChevronUp, ChevronDown } from 'lucide-react';
import { FaTimes, FaPlus } from "react-icons/fa";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import "react-tabs/style/react-tabs.css";
import '../ProfileQuestions/ProfileQuestions.css';
import './PregnancyTracking.css';
import { Dropdown } from "react-bootstrap";
import PregnancyTrackingModal from './PregnancyTrackingModal';

const defaultFormState = {
    week: '',
    month: '',
    trimester: '',
    status: 'A',
    weekStatus: 'completed',
    journeyCard: {
        title: '',
        description: '',
        fruitComparison: '',
        babyLength: '',
        babyWeight: '',
        sizeSummary: '',
        stageImageName: '',
        weeklyInsight: '',
    },
    details: {
        babyDevelopment: { title: '', description: '', highlights: [{ title: '', description: '' }] },
        momChanges: { title: '', description: '', items: [{ title: '', description: '' }] },
        whoAlignedCare: {
            ancContact: '',
            keyRecommendation: '',
            nutritionStandard: '',
            clinicalAssessment: '',
            preventiveIntervention: ''
        },
        checkups: [{ title: '', type: '', description: '', whatToExpect: [''] }],
        tests: [{ name: '', category: '', description: '', purpose: '', preparation: '', isRoutine: true }],
        ultrasound: [{ title: '', description: '', purpose: '', preparation: '' }],
        nutrition: { title: '', description: '', recommendations: [''] },
        lifestyle: [''],
        warningSigns: [{ title: '', description: '' }],
        doctorQuestions: [''],
        weeklyTip: { title: '', description: '' },
        customHTML: ''
    },
    weekCompleted: {
        Completed: false,
        CompletedBy: '',
        CompletedDate: ''
    },
    disclaimer: '',
    createdAt: new Date(),
    updatedAt: new Date()
};

const PregnancyTracking = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [isViewMode, setIsViewMode] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [filterWeek, setFilterWeek] = useState('');
    const [filterMonth, setFilterMonth] = useState('');
    const [filterTrimester, setFilterTrimester] = useState('');
    const [filterStatus, setFilterStatus] = useState('');

    // Pagination & Sorting
    const [totalCount, setTotalCount] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortBy, setSortBy] = useState('latest');
    const [sortOrder, setSortOrder] = useState(-1);

    const searchTimeoutRef = useRef(null);

    const [formData, setFormData] = useState(JSON.parse(JSON.stringify(defaultFormState)));
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [tabIndex, setTabIndex] = useState(0);
    const [showReviewTab, setShowReviewTab] = useState(false);
    const [errors, setErrors] = useState({});

    const userId = localStorage.getItem('id');

    // Debounce search
    useEffect(() => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
    }, [searchTerm]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const payload = {
                limit: itemsPerPage,
                page: currentPage,
                search: debouncedSearch,
                week: filterWeek,
                month: filterMonth,
                trimester: filterTrimester,
                weekStatus: filterStatus,
                sortBy: sortBy,
                sortOrder: sortOrder
            };
            const encryptedPayload = AesEncrypt(payload);
            const response = await axios.post(`${WebService}api/get_all_pregnancy_tracking`, { EncData: encryptedPayload });
            if (response.data.response === 'S') {
                const decrypted = AesDecrypt(response.data.data);
                setData(decrypted?.data || []);
                setTotalCount(decrypted?.totalCount || 0);
            } else {
                setData([]);
                setTotalCount(0);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [debouncedSearch, filterWeek, filterMonth, filterTrimester, filterStatus, currentPage, itemsPerPage, sortBy, sortOrder]);

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === -1 ? 1 : -1);
        } else {
            setSortBy(field);
            setSortOrder(1);
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });
        if (!result.isConfirmed) return;
        try {
            const response = await axios.post(`${WebService}api/delete_pregnancy_tracking`, { EncData: AesEncrypt({ _id: id }) });
            if (response.data.response === 'S') {
                Swal.fire('Deleted!', 'Record deleted successfully.', 'success');
                fetchData();
            } else {
                Swal.fire('Error', response.data.message, 'error');
            }
        } catch (err) {
            Swal.fire('Error', 'Failed to delete record', 'error');
        }
    };

    const openAddModal = () => {
        setIsViewMode(false);
        setEditingItem(null);
        setFormData(JSON.parse(JSON.stringify(defaultFormState)));
        setImageFile(null);
        setImagePreview('');
        setTabIndex(0);
        setShowReviewTab(false);
        setErrors({});
        setShowModal(true);
    };

    const openEditModal = (item) => {
        setIsViewMode(false);
        setEditingItem(item);

        // Ensure nested objects exist to prevent crashes during editing
        const safeData = JSON.parse(JSON.stringify(defaultFormState));
        const mergedData = { ...safeData, ...item };
        mergedData.details = { ...safeData.details, ...(item.details || {}) };
        mergedData.details.babyDevelopment = { ...safeData.details.babyDevelopment, ...(item.details?.babyDevelopment || {}) };
        mergedData.details.momChanges = { ...safeData.details.momChanges, ...(item.details?.momChanges || {}) };
        mergedData.details.whoAlignedCare = { ...safeData.details.whoAlignedCare, ...(item.details?.whoAlignedCare || {}) };
        mergedData.details.nutrition = { ...safeData.details.nutrition, ...(item.details?.nutrition || {}) };
        mergedData.details.lifestyle = Array.isArray(item.details?.lifestyle) ? item.details.lifestyle : [];
        mergedData.details.weeklyTip = { ...safeData.details.weeklyTip, ...(item.details?.weeklyTip || {}) };

        const ensureOneItem = (arr, defaultItem) => (Array.isArray(arr) && arr.length > 0) ? arr : [defaultItem];
        mergedData.details.babyDevelopment.highlights = ensureOneItem(mergedData.details.babyDevelopment.highlights, { title: '', description: '' });
        mergedData.details.momChanges.items = ensureOneItem(mergedData.details.momChanges.items, { title: '', description: '' });
        mergedData.details.checkups = ensureOneItem(mergedData.details.checkups, { title: '', type: '', description: '', whatToExpect: [''] });
        mergedData.details.tests = ensureOneItem(mergedData.details.tests, { name: '', category: '', description: '', purpose: '', preparation: '', isRoutine: true });
        mergedData.details.ultrasound = ensureOneItem(mergedData.details.ultrasound, { title: '', description: '', purpose: '', preparation: '' });
        mergedData.details.nutrition.recommendations = ensureOneItem(mergedData.details.nutrition.recommendations, '');
        mergedData.details.lifestyle = ensureOneItem(mergedData.details.lifestyle, '');
        mergedData.details.warningSigns = ensureOneItem(mergedData.details.warningSigns, { title: '', description: '' });
        mergedData.details.doctorQuestions = ensureOneItem(mergedData.details.doctorQuestions, '');

        setFormData(mergedData);
        setImageFile(null);
        setImagePreview(item.journeyCard?.stageimageUrl || '');
        setTabIndex(0);
        setTabIndex(0);
        setErrors({});
        setShowModal(true);
    };

    const openViewModal = (item) => {
        openEditModal(item);
        setIsViewMode(true);
    };

    const handlePublish = async (id) => {
        const result = await Swal.fire({
            title: 'Confirm Publish',
            text: 'Are you sure you want to publish this record?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Publish',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const userId = localStorage.getItem('id') || localStorage.getItem('adminId') || '';
                const encryptedPayload = AesEncrypt({ _id: id, userId });
                const response = await axios.post(`${WebService}api/publish_pregnancy_tracking`, { EncData: encryptedPayload });
                if (response.data.response === 'S') {
                    Swal.fire('Published!', 'The record has been published.', 'success');
                    fetchData();
                } else {
                    Swal.fire('Error', response.data.message || 'Failed to publish record.', 'error');
                }
            } catch (error) {
                console.error(error);
                Swal.fire('Error', 'An error occurred while publishing.', 'error');
            }
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setImagePreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const validateTabs = () => {
        let missingFieldsByTab = {};
        const addMissing = (tabName, fieldName) => {
            if (!missingFieldsByTab[tabName]) missingFieldsByTab[tabName] = [];
            missingFieldsByTab[tabName].push(fieldName);
        };

        // Tab 1: Basic & Journey Card
        if (!formData.week) addMissing('Basic & Journey Card', 'Week');
        if (!formData.month) addMissing('Basic & Journey Card', 'Month');
        if (!formData.trimester) addMissing('Basic & Journey Card', 'Trimester');
        if (!formData.journeyCard?.title) addMissing('Basic & Journey Card', 'Journey Card Title');
        if (!formData.journeyCard?.fruitComparison) addMissing('Basic & Journey Card', 'Fruit Comparison');
        if (!formData.journeyCard?.description) addMissing('Basic & Journey Card', 'Journey Card Description');
        if (!formData.journeyCard?.babyLength) addMissing('Basic & Journey Card', 'Baby Length');
        if (!formData.journeyCard?.babyWeight) addMissing('Basic & Journey Card', 'Baby Weight');
        if (!formData.journeyCard?.stageImageName) addMissing('Basic & Journey Card', 'Stage Image Name');
        if (!formData.journeyCard?.stageimageUrl && !imageFile && !imagePreview) addMissing('Basic & Journey Card', 'Stage Image');
        if (!formData.journeyCard?.sizeSummary) addMissing('Basic & Journey Card', 'Size Summary');
        if (!formData.journeyCard?.weeklyInsight) addMissing('Basic & Journey Card', 'Weekly Insight');

        // Tab 2: Baby & Maternal Changes
        if (!formData.details?.babyDevelopment?.title) addMissing('Baby & Maternal Changes', 'Baby Development Title');
        if (!formData.details?.babyDevelopment?.description) addMissing('Baby & Maternal Changes', 'Baby Development Description');
        if (!formData.details?.babyDevelopment?.highlights?.length) addMissing('Baby & Maternal Changes', 'Baby Highlights');
        else {
            const hasInvalidHighlight = formData.details.babyDevelopment.highlights.some(h => !h.title?.trim() || !h.description?.trim());
            if (hasInvalidHighlight) addMissing('Baby & Maternal Changes', 'Baby Highlights (incomplete)');
        }

        if (!formData.details?.momChanges?.title) addMissing('Baby & Maternal Changes', 'Maternal Changes Title');
        if (!formData.details?.momChanges?.description) addMissing('Baby & Maternal Changes', 'Maternal Changes Description');
        if (!formData.details?.momChanges?.items?.length) addMissing('Baby & Maternal Changes', 'Maternal Changes Items');
        else {
            const hasInvalidItem = formData.details.momChanges.items.some(i => !i.title?.trim() || !i.description?.trim());
            if (hasInvalidItem) addMissing('Baby & Maternal Changes', 'Maternal Changes Items (incomplete)');
        }

        // Tab 3: Medical & Care
        if (!formData.details?.whoAlignedCare?.ancContact) addMissing('Medical & Care', 'WHO Aligned Care (ANC Contact)');
        if (!formData.details?.whoAlignedCare?.keyRecommendation) addMissing('Medical & Care', 'WHO Aligned Care (Key Recommendation)');
        if (!formData.details?.whoAlignedCare?.nutritionStandard) addMissing('Medical & Care', 'WHO Aligned Care (Nutrition Standard)');
        if (!formData.details?.whoAlignedCare?.clinicalAssessment) addMissing('Medical & Care', 'WHO Aligned Care (Clinical Assessment)');
        if (!formData.details?.whoAlignedCare?.preventiveIntervention) addMissing('Medical & Care', 'WHO Aligned Care (Preventive Intervention)');

        if (!formData.details?.checkups?.length) addMissing('Medical & Care', 'Checkups');
        else {
            const hasInvalidCheckup = formData.details.checkups.some(c => !c.title?.trim() || !c.type?.trim() || !c.description?.trim() || !c.whatToExpect?.length || c.whatToExpect.some(w => !w.trim()));
            if (hasInvalidCheckup) addMissing('Medical & Care', 'Checkups (incomplete)');
        }

        if (!formData.details?.tests?.length) addMissing('Medical & Care', 'Tests');
        else {
            const hasInvalidTest = formData.details.tests.some(t => !t.name?.trim() || !t.category?.trim() || !t.description?.trim() || !t.purpose?.trim() || !t.preparation?.trim());
            if (hasInvalidTest) addMissing('Medical & Care', 'Tests (incomplete)');
        }

        if (!formData.details?.ultrasound?.length) addMissing('Medical & Care', 'Ultrasound');
        else {
            const hasInvalidUs = formData.details.ultrasound.some(u => !u.title?.trim() || !u.description?.trim() || !u.purpose?.trim() || !u.preparation?.trim());
            if (hasInvalidUs) addMissing('Medical & Care', 'Ultrasound (incomplete)');
        }

        // Tab 4: Lifestyle & Misc
        if (!formData.details?.nutrition?.title) addMissing('Lifestyle & Misc', 'Nutrition Title');
        if (!formData.details?.nutrition?.description) addMissing('Lifestyle & Misc', 'Nutrition Description');
        if (!formData.details?.nutrition?.recommendations?.length) addMissing('Lifestyle & Misc', 'Nutrition Recommendations');
        else if (formData.details.nutrition.recommendations.every(r => !r.trim())) addMissing('Lifestyle & Misc', 'Nutrition Recommendations (empty)');

        if (!formData.details?.weeklyTip?.title) addMissing('Lifestyle & Misc', 'Weekly Tip Title');
        if (!formData.details?.weeklyTip?.description) addMissing('Lifestyle & Misc', 'Weekly Tip Description');

        if (!formData.details?.lifestyle?.length) addMissing('Lifestyle & Misc', 'Lifestyle Details');
        else if (formData.details.lifestyle.every(l => !l.trim())) addMissing('Lifestyle & Misc', 'Lifestyle Details (empty)');

        if (!formData.details?.doctorQuestions?.length) addMissing('Lifestyle & Misc', 'Doctor Questions');
        else if (formData.details.doctorQuestions.every(q => !q.trim())) addMissing('Lifestyle & Misc', 'Doctor Questions (empty)');

        if (!formData.details?.warningSigns?.length) addMissing('Lifestyle & Misc', 'Warning Signs');
        else {
            const hasValidWS = formData.details.warningSigns.some(ws => ws.title || ws.description);
            if (!hasValidWS) addMissing('Lifestyle & Misc', 'Warning Signs (empty)');
        }

        return missingFieldsByTab;
    };

    const handleNext = async () => {
        const missingFieldsByTab = validateTabs();
        const tabNames = ['Basic & Journey Card', 'Baby & Maternal Changes', 'Medical & Care', 'Lifestyle & Misc'];
        const currentTabName = tabNames[tabIndex];

        if (missingFieldsByTab[currentTabName] && missingFieldsByTab[currentTabName].length > 0) {
            const newErrors = {};
            missingFieldsByTab[currentTabName].forEach(field => {
                newErrors[field] = 'Required to fill this field';
            });
            setErrors(newErrors);
            Swal.fire({ 
                title: 'Required Fields', 
                text: 'Please fill all the required fields in this tab before proceeding.', 
                icon: 'warning', 
                customClass: { container: 'my-swal-container' } 
            });
            return;
        }

        if (tabIndex === 0 && formData.week) {
            try {
                const payload = {
                    week: formData.week,
                    excludeId: editingItem ? editingItem._id : null
                };
                const encryptedPayload = AesEncrypt(payload);
                const response = await axios.post(`${WebService}api/check_week_exists`, { EncData: encryptedPayload });
                if (response.data.response === 'S' && response.data.exists) {
                    setErrors(prev => ({ ...prev, 'Week': `Week ${formData.week} already exists.` }));
                    Swal.fire({ 
                        title: 'Duplicate Week', 
                        text: `The week number ${formData.week} already exists . Please verify and enter a unique week number.`, 
                        icon: 'error', 
                        customClass: { container: 'my-swal-container' } 
                    });
                    return;
                }
            } catch (error) {
                console.error("Error checking week existence", error);
            }
        }

        setErrors({});
        if (tabIndex < 3) {
            setTabIndex(prev => prev + 1);
        }
    };

    const handleSave = async () => {
        const missingFieldsByTab = validateTabs();
        const currentTabName = 'Lifestyle & Misc';

        if (missingFieldsByTab[currentTabName] && missingFieldsByTab[currentTabName].length > 0) {
            const newErrors = {};
            missingFieldsByTab[currentTabName].forEach(field => {
                newErrors[field] = 'Required to fill this field';
            });
            setErrors(newErrors);
            Swal.fire({ 
                title: 'Required Fields', 
                text: 'Please fill all the required fields in this tab before saving.', 
                icon: 'warning', 
                customClass: { container: 'my-swal-container' } 
            });
            return;
        }

        setErrors({});

        let finalStatus = formData.status || 'A';
        let finalWeekStatus = 'completed';

        try {
            const submitData = new FormData();
            if (imageFile) {
                submitData.append('stageimage', imageFile);
            }

            const userId = localStorage.getItem('id') || localStorage.getItem('adminId') || '';
            const payload = { ...formData, userId, status: finalStatus, weekStatus: finalWeekStatus };

            payload.weekCompleted = {
                ...payload.weekCompleted,
                Completed: true,
                CompletedDate: new Date().toISOString()
            };

            if (payload.details && (!payload.details.customHTML || payload.details.customHTML.trim() === '' || payload.details.customHTML === '<p><br></p>')) {
                delete payload.details.customHTML;
            }

            delete payload.journeyCard?.stageimageUrl;

            const encryptedPayload = AesEncrypt(payload);
            submitData.append('EncData', JSON.stringify(encryptedPayload));

            const endpoint = editingItem ? 'edit_pregnancy_tracking' : 'add_pregnancy_tracking';
            const response = await axios.post(`${WebService}api/${endpoint}`, submitData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (response.data.response === 'S') {
                fetchData();
                Swal.fire({ title: 'Success', text: 'All fields are successfully completed and the record has been updated.', icon: 'success', customClass: { container: 'my-swal-container' } });
                setShowModal(false);
            } else {
                Swal.fire({ title: 'Error', text: response.data.message, icon: 'error', customClass: { container: 'my-swal-container' } });
            }
        } catch (err) {
            console.error(err);
            Swal.fire({ title: 'Error', text: 'Failed to save record', icon: 'error', customClass: { container: 'my-swal-container' } });
        }
    };

    const SortIcon = ({ field }) => {
        if (sortBy !== field) return <ChevronDown size={14} className="text-muted" />;
        return sortOrder === 1 ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
    };

    const updateDetailsField = (section, field, value) => {
        setFormData(prev => ({
            ...prev,
            details: {
                ...prev.details,
                [section]: {
                    ...prev.details[section],
                    [field]: value
                }
            }
        }));
    };

    const updateRootDetailsField = (field, value) => {
        setFormData(prev => ({
            ...prev,
            details: {
                ...prev.details,
                [field]: value
            }
        }));
    };

    // Generic Array Handlers
    const addArrayItem = (path, defaultItem) => {
        setFormData(prev => {
            const keys = path.split('.');
            const newForm = { ...prev };
            let curr = newForm;
            for (let i = 0; i < keys.length - 1; i++) {
                curr[keys[i]] = { ...curr[keys[i]] };
                curr = curr[keys[i]];
            }
            curr[keys[keys.length - 1]] = [...(curr[keys[keys.length - 1]] || []), defaultItem];
            return newForm;
        });
    };

    const removeArrayItem = (path, index) => {
        setFormData(prev => {
            const keys = path.split('.');
            const newForm = { ...prev };
            let curr = newForm;
            for (let i = 0; i < keys.length - 1; i++) {
                curr[keys[i]] = { ...curr[keys[i]] };
                curr = curr[keys[i]];
            }
            const arr = [...curr[keys[keys.length - 1]]];
            arr.splice(index, 1);
            curr[keys[keys.length - 1]] = arr;
            return newForm;
        });
    };

    const updateArrayItem = (path, index, field, value) => {
        setFormData(prev => {
            const keys = path.split('.');
            const newForm = { ...prev };
            let curr = newForm;
            for (let i = 0; i < keys.length - 1; i++) {
                curr[keys[i]] = { ...curr[keys[i]] };
                curr = curr[keys[i]];
            }
            const arr = [...curr[keys[keys.length - 1]]];
            arr[index] = { ...arr[index], [field]: value };
            curr[keys[keys.length - 1]] = arr;
            return newForm;
        });
    };

    const totalPages = Math.ceil(totalCount / itemsPerPage);

    const statusDropdownOptions = [
        { value: '', label: 'All', bullet: 'transparent', bg: 'transparent' },
        { value: 'completed', label: 'Completed', bullet: '#ffc107', bg: '#fff3cd' },
        { value: 'published', label: 'Published', bullet: '#198754', bg: '#d1e7dd' }
    ];

    return (
        <div className="container-fluid" style={{ padding: '20px' }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0" style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Pregnancy Tracking</h4>
                <button className="btn btn-primary" style={{ width: '150px', maxWidth: '150px', minWidth: '150px', flex: '0 0 auto', padding: '10px 20px' }} onClick={openAddModal}>+ Add</button>
            </div>

            <div className="card mb-4">
                <div className="card-body py-2">
                    <div className="row g-3 align-items-center">
                        <div className="col-md-3">
                            <label className="form-label mb-1">Search (Title/Desc)</label>
                            <input type="text" className="form-control" style={{ padding: '0.25rem 0.5rem', height: '35px' }} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search..." />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label mb-1">Week</label>
                            <input type="number" className="form-control" style={{ padding: '0.25rem 0.5rem', height: '35px' }} value={filterWeek} onChange={(e) => setFilterWeek(e.target.value)} placeholder="e.g. 7" />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label mb-1">Month</label>
                            <input type="number" className="form-control" style={{ padding: '0.25rem 0.5rem', height: '35px' }} value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} placeholder="e.g. 2" />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label mb-1">Trimester</label>
                            <input type="number" className="form-control" style={{ padding: '0.25rem 0.5rem', height: '35px' }} value={filterTrimester} onChange={(e) => setFilterTrimester(e.target.value)} placeholder="e.g. 1" />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label mb-1">Status</label>
                            <Dropdown>
                                {(() => {
                                    const selectedOpt = statusDropdownOptions.find(opt => opt.value === filterStatus) || statusDropdownOptions[0];
                                    return (
                                        <Dropdown.Toggle variant="light" className="w-100 text-start d-flex justify-content-between align-items-center bg-white border" style={{ padding: '0.25rem 0.5rem', height: '35px', color: '#000', fontSize: '14px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                {selectedOpt.value !== '' && (
                                                    <span style={{
                                                        display: 'inline-block',
                                                        width: '10px',
                                                        height: '10px',
                                                        backgroundColor: selectedOpt.bullet,
                                                        borderRadius: '50%',
                                                        marginRight: '8px'
                                                    }}></span>
                                                )}
                                                <span style={{ color: '#000', opacity: 1, visibility: 'visible', fontSize: '14px', textTransform: 'none' }}>{selectedOpt.label}</span>
                                            </div>
                                        </Dropdown.Toggle>
                                    );
                                })()}
                                <Dropdown.Menu className="w-100 p-2 shadow-sm" style={{ border: 'none', borderRadius: '12px', minWidth: '100%' }}>
                                    {statusDropdownOptions.map((opt, idx) => (
                                        <Dropdown.Item
                                            key={idx}
                                            onClick={() => setFilterStatus(opt.value)}
                                            style={{
                                                backgroundColor: opt.bg,
                                                color: '#000',
                                                borderRadius: '8px',
                                                marginBottom: '6px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                fontWeight: '500',
                                                padding: '8px 12px'
                                            }}
                                        >
                                            <span style={{
                                                display: 'inline-block',
                                                width: '12px',
                                                height: '12px',
                                                backgroundColor: opt.bullet,
                                                borderRadius: '50%',
                                                marginRight: '12px'
                                            }}></span>
                                            {opt.label}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                        {(searchTerm !== '' || filterWeek !== '' || filterMonth !== '' || filterTrimester !== '' || filterStatus !== '') && (
                            <div className="col-md-1 d-flex align-items-end" style={{ paddingBottom: '1px' }}>
                                <button
                                    className="btn btn-danger text-white w-100"
                                    style={{ height: '35px', padding: '0.25rem 0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    onClick={() => {
                                        setSearchTerm('');
                                        setFilterWeek('');
                                        setFilterMonth('');
                                        setFilterTrimester('');
                                        setFilterStatus('');
                                    }}
                                >
                                    <i className="fa-solid fa-xmark me-1"></i> Clear
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="pq-table-wrapper">
                <div className="pq-table-scroll">
                    <table className="pq-table">
                        <thead>
                            <tr>
                                <th onClick={() => handleSort('trimester')} style={{ textAlign: 'center', cursor: 'pointer' }}>Trimester <SortIcon field="trimester" /></th>
                                <th onClick={() => handleSort('weekMonth')} style={{ textAlign: 'center', cursor: 'pointer' }}>Week / Month <SortIcon field="weekMonth" /></th>
                                <th onClick={() => handleSort('title')} style={{ cursor: 'pointer' }}>Title <SortIcon field="title" /></th>
                                <th onClick={() => handleSort('babyLength')} style={{ textAlign: 'center', cursor: 'pointer' }}>Baby Length <SortIcon field="babyLength" /></th>
                                <th onClick={() => handleSort('babyWeight')} style={{ textAlign: 'center', cursor: 'pointer' }}>Baby Weight <SortIcon field="babyWeight" /></th>
                                <th style={{ textAlign: 'center' }}>Status</th>
                                <th style={{ textAlign: 'center' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="7">
                                        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '54vh' }}>
                                            Loading...
                                        </div>
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    <td colSpan="7">
                                        <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
                                            <h5 style={{ color: '#6e7480ff', fontWeight: 'bold' }}>No Record Found</h5>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                data.map((item) => (
                                    <tr key={item._id}>
                                        <td style={{ textAlign: 'center' }}>{item.trimester}</td>
                                        <td style={{ textAlign: 'center' }}>W {item.week} / M {item.month}</td>
                                        <td title={item.journeyCard?.title}>
                                            {item.journeyCard?.title?.length > 12
                                                ? `${item.journeyCard.title.substring(0, 12)}...`
                                                : item.journeyCard?.title}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>{item.journeyCard?.babyLength}</td>
                                        <td style={{ textAlign: 'center' }}>{item.journeyCard?.babyWeight}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            <span className={`badge ${item.weekStatus === 'published' ? 'badge-soft-success' : item.weekStatus === 'completed' ? 'badge-soft-orange' : 'badge-soft-secondary'}`} style={{ fontSize: '12px', padding: '0.5em 0.75em', textTransform: 'capitalize' }}>
                                                {item.weekStatus}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="d-flex justify-content-center gap-2">
                                                {['completed', 'published'].includes(item.weekStatus) && (
                                                    <button className="pq-action-btn" style={{ backgroundColor: '#17a2b8', color: 'white' }} onClick={() => openViewModal(item)} title="View">
                                                        <i className="fas fa-eye"></i>
                                                    </button>
                                                )}
                                                <button className="pq-action-btn" onClick={() => openEditModal(item)} title="Edit">
                                                    <i className="fas fa-pen"></i>
                                                </button>
                                                <button className="pq-action-btn pq-action-btn-danger" onClick={() => handleDelete(item._id)} title="Delete">
                                                    <i className="fa fa-trash"></i>
                                                </button>
                                                {item.weekStatus === 'completed' && (
                                                    <button className="pq-action-btn" style={{ backgroundColor: '#28a745', color: 'white' }} onClick={() => handlePublish(item._id)} title="Publish">
                                                        <i className="fas fa-check"></i>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                <div className="pq-pagination-container d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                        <span className="pq-pagination-text me-2">Showing {totalCount > 0 ? ((currentPage - 1) * itemsPerPage) + 1 : 0} – {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} entries. Per page: </span>
                        <select className="form-select form-select-sm pq-filter-input" value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} style={{ width: '70px', display: 'inline-block', height: '32px !important', padding: '0 10px !important' }}>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                        </select>
                    </div>

                    <ul className="pagination pagination-sm mb-0">
                        <li className={`page-item pq-page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link pq-page-link" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                                &laquo;
                            </button>
                        </li>

                        {[...Array(totalPages || 0)].map((_, i) => {
                            if (i === 0 || i === (totalPages || 1) - 1 || (i >= currentPage - 2 && i <= currentPage)) {
                                return (
                                    <li className={`page-item pq-page-item ${i + 1 === currentPage ? 'active' : ''}`} key={i}>
                                        <button className="page-link pq-page-link" onClick={() => handlePageChange(i + 1)}>
                                            {i + 1}
                                        </button>
                                    </li>
                                );
                            }
                            if (i === 1 && currentPage > 3) return <li className="page-item pq-page-item disabled" key="start-ellipsis"><span className="page-link pq-page-link">...</span></li>;
                            if (i === (totalPages || 1) - 2 && currentPage < (totalPages || 1) - 2) return <li className="page-item pq-page-item disabled" key="end-ellipsis"><span className="page-link pq-page-link">...</span></li>;
                            return null;
                        })}

                        <li className={`page-item pq-page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                            <button className="page-link pq-page-link" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0}>
                                &raquo;
                            </button>
                        </li>
                    </ul>
                </div>
            </div>
            {/* Add / Edit Modal */}
            <PregnancyTrackingModal
                showModal={showModal}
                setShowModal={setShowModal}
                isViewMode={isViewMode}
                editingItem={editingItem}
                tabIndex={tabIndex}
                setTabIndex={setTabIndex}
                handleNext={handleNext}
                imagePreview={imagePreview}
                handleImageChange={handleImageChange}
                formData={formData}
                setFormData={setFormData}
                errors={errors}
                updateDetailsField={updateDetailsField}
                updateRootDetailsField={updateRootDetailsField}
                addArrayItem={addArrayItem}
                removeArrayItem={removeArrayItem}
                updateArrayItem={updateArrayItem}
                handleSave={handleSave}
            />
        </div>
    );
};

export default PregnancyTracking;
